import { isAfter, isFuture } from "date-fns";

import type { EventCategoryDto, EventDetailsDto, EventDto } from "@/api/types";
import type { TranslationFunction } from "@/translations";

export function getEventCategoryName(t: TranslationFunction, key: EventCategoryDto["id"] | "all"): string {
  switch (key) {
    case "all":
      return t("model.event.category.all");
    case "maintenance":
      return t("model.event.category.maintenance");
    case "social":
      return t("model.event.category.social");
    case "sport":
      return t("model.event.category.sport");
    case "unknown":
    default:
      return t("model.event.category.other");
  }
}

export function canParticipateEvent(event: EventDto | EventDetailsDto): boolean {
  const isUpcomingOrInProgress = isAfter(new Date(event.endsAt), new Date());

  // It's an upcoming external event with a URL
  if (isUpcomingOrInProgress && event.type === "external" && event.externalPartyUrl) {
    return true;
  }

  // For upcoming or in-progress internal events, show the participation/sign-off CTA
  // when the user can participate or is already signed up (so they can leave, even if full)
  if (isUpcomingOrInProgress && event.type !== "external" && (event.canParticipate || event.isSignedUp)) {
    return true;
  }

  return false;
}

export function getIsEventSharable(event: EventDto | EventDetailsDto): boolean {
  return Boolean(event.publishedAt) && isFuture(event.endsAt);
}

export function computeMaxGuests(event: EventDto | EventDetailsDto): number {
  let maxGuests = event.maxGuestsPerParticipant ?? Infinity;
  if (event.maxParticipants !== undefined && event.participantCount !== undefined) {
    const remainingSpots = event.maxParticipants - event.participantCount;
    if (maxGuests > remainingSpots) {
      maxGuests = remainingSpots;
    }
  }

  return Math.min(maxGuests, 5);
}

export function computeMaxGuestsForNewParticipant(event: EventDto | EventDetailsDto): number {
  const remainingCapacity =
    event.maxParticipants === undefined || event.participantCount === undefined
      ? Infinity
      : event.maxParticipants - event.participantCount - 1;

  return Math.max(0, Math.min(event.maxGuestsPerParticipant ?? Infinity, remainingCapacity, 5));
}

export function computeMaxGuestsForExistingParticipant(
  event: EventDto | EventDetailsDto,
  currentGuestCount: number,
): number {
  const remainingCapacity =
    event.maxParticipants === undefined || event.participantCount === undefined
      ? Infinity
      : event.maxParticipants - event.participantCount + currentGuestCount;

  return Math.max(0, Math.min(event.maxGuestsPerParticipant ?? Infinity, remainingCapacity, 5));
}
